import * as Yup from 'yup';
import { stepOneSchema } from '../utils/Validations/stepOneValidation';
import { stepTwoSchema } from '../utils/Validations/stepTwoValidation';
import { stepThreeSchema } from '../utils/Validations/stepThreeValidation';
import { stepFourSchema } from '../utils/Validations/stepFourValidation';
import { stepFiveSchema } from '../utils/Validations/stepFiveValidation';

export const stepSchemas = {
  0: stepOneSchema,
  1: stepTwoSchema,
  2: stepThreeSchema,
  3: stepFourSchema,
  4: stepFiveSchema,
};

export const buildYupSchema = (fields, stepIndex) => {
  const shape = {};

  const addFieldToSchema = (field) => {


    const coBorrowerPastEmploymentFields = [
      'co_borrower_past_employer',
      'co_borrower_past_occupation',
      'co_borrower_past_hire_date',
      'co_borrower_past_last_day_of_employment_date',
    ];
    
    if (coBorrowerPastEmploymentFields.includes(field.name)) {
      shape[field.name] = Yup.mixed().when('co_borrower_employer_since', {
        is: (val) => {
          if (!val) return false;
          const date = new Date(val);
          if (isNaN(date)) return false;
          const twoYearsAgo = new Date();
          twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
          return date > twoYearsAgo;
        },
        then: () => {
          if (field.type === 'date') {
            return Yup.date()
              .required(`${field.label} is required`)
              .typeError(`${field.label} must be a valid date`);
          } else {
            return Yup.string().required(`${field.label} is required`);
          }
        },
        otherwise: () => Yup.mixed().notRequired()
      });
      return;
    }
    
    //  PRIORITY: Handle past address fields based on 'address_since'
    const pastFields = [
      'past_address_1',
      'past_address_2',
      'past_city',
      'past_state',
      'past_zip',
      'past_movein_date',
      'past_moveout_date'
    ];

    if (pastFields.includes(field.name)) {
      const isDateField = field.type === 'date';
      let validator = Yup.mixed().when('address_since', {
        is: (val) => {
          if (!val) return false;
          const date = new Date(val);
          if (isNaN(date)) return false;
          const twoYearsAgo = new Date();
          twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
          return date > twoYearsAgo;
        },
        then: () => {
          if (isDateField) {
            return Yup.date()
              .required(`${field.label} is required`)
              .typeError(`${field.label} must be a valid date`)
              .max(new Date(), `${field.label} must be in the past`)
              .max(Yup.ref('address_since'), `${field.label} must be before current address start date`);
          } else {
            return Yup.string().required(`${field.label} is required`);
          }
        },
        otherwise: () => Yup.mixed().notRequired()
      });
    
      shape[field.name] = validator;
      return;
    }
    
    
    
    const pastEmploymentFields = [
      'past_employer',
      'past_occupation',
      'past_hire_date',
      'past_last_day_of_employment_date'
    ];
    
    if (pastEmploymentFields.includes(field.name)) {
      let validator = Yup.mixed().when('employer_since', {
        is: (val) => {
          if (!val) return false;
          const date = new Date(val);
          if (isNaN(date)) return false;
          const twoYearsAgo = new Date();
          twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);
          return date > twoYearsAgo;
        },
        then: () => {
          if (field.type === 'date') {
            return Yup.date()
              .required(`${field.label} is required`)
              .typeError(`${field.label} must be a valid date`);
          } else {
            return Yup.string().required(`${field.label} is required`);
          }
        },
        otherwise: () => Yup.mixed().notRequired()
      });
    
      shape[field.name] = validator;
      return;
    }
    

    if (!field.name) return;

    let validator;

    const primaryEmployerFields = [
      'employer',
      'job_title',
      'employer_phone_1',
      'employer_address_1',
      'employer_city',
      'employer_state',
      'employer_zip',
      'employer_since',
    ];

    if (primaryEmployerFields.includes(field.name)) {
      validator = Yup.string().when('employment_status', {
        is: (val) => ['full-time', 'part-time', 'self-employed'].includes(val),
        then: (schema) => {
          let baseSchema = Yup.string().required(`${field.label} is required`);
          if (field.maxlength) {
            baseSchema = baseSchema.max(field.maxlength, `${field.label} must be at most ${field.maxlength} characters`);
          }
          if (field.validationPattern) {
            baseSchema = baseSchema.matches(new RegExp(field.validationPattern), field.validationMessage || `${field.label} is invalid`);
          }
          return baseSchema;
        },
        otherwise: (schema) => schema.notRequired(),
      });
    }

    if (field.name.startsWith('co_borrower_')) {
      if (
        [
          'co_borrower_employer',
          'co_borrower_employer_since',
          'co_borrower_job_title',
          'co_borrower_employer_phone_1',
          'co_borrower_employer_address_1',
          'co_borrower_employer_city',
          'co_borrower_employer_state',
          'co_borrower_employer_zip',
        ].includes(field.name)
      ) {
        validator = Yup.mixed().when(['coapplicant', 'co_borrower_employment_status'], {
          is: (coapplicant, employmentStatus) =>
            coapplicant === 'Yes' && ['full-time', 'part-time'].includes(employmentStatus),
          then: () => {
            let nestedValidator;
            if (field.type === 'text') {
              nestedValidator = Yup.string().required(`${field.label} is required`);
              if (field.maxlength) {
                nestedValidator = nestedValidator.max(
                  field.maxlength,
                  `${field.label} must be at most ${field.maxlength} characters`
                );
              }
              if (field.validationPattern) {
                nestedValidator = nestedValidator.matches(
                  new RegExp(field.validationPattern),
                  field.validationMessage || `${field.label} is invalid`
                );
              }
            } else if (field.type === 'number') {
              nestedValidator = Yup.number()
                .typeError(`${field.label} must be a number`)
                .required(`${field.label} is required`);
              if (field.maxlength) {
                nestedValidator = nestedValidator.max(
                  parseInt('9'.repeat(field.maxlength)),
                  `${field.label} must be at most ${field.maxlength} digits`
                );
              }
            } else if (field.type === 'date') {
              nestedValidator = Yup.date()
                .nullable()
                .transform((value, originalValue) => (originalValue === '' ? null : value))
                .typeError(`${field.label} must be a valid date`)
                .required(`${field.label} is required`);
              if (field.validation === 'validateIssueDate') {
                nestedValidator = nestedValidator.max(new Date(), `${field.label} must be in the past`);
              }
            } else if (field.type === 'select') {
              nestedValidator = Yup.string().required(`${field.label} is required`);
            }
            return nestedValidator;
          },
          otherwise: () => Yup.mixed().nullable().notRequired(),
        });
      } else {
        validator = Yup.mixed().when('coapplicant', {
          is: 'Yes',
          then: () => {
            let baseValidator;
            if (field.type === 'text' || field.type === 'email' || field.type === 'textarea') {
              baseValidator = Yup.string();
              if (field.required) {
                baseValidator = baseValidator.required(`${field.label} is required`);
              }
              if (field.validationPattern) {
                baseValidator = baseValidator.matches(
                  new RegExp(field.validationPattern),
                  field.validationMessage || `${field.label} is invalid`
                );
              }
              if (field.type === 'email') {
                baseValidator = baseValidator.email(`${field.label} must be a valid email`);
              }
              if (field.maxlength) {
                baseValidator = baseValidator.max(
                  field.maxlength,
                  `${field.label} must be at most ${field.maxlength} characters`
                );
              }
            } else if (field.type === 'number') {
              baseValidator = Yup.number().typeError(`${field.label} must be a number`);
              if (field.required) {
                baseValidator = baseValidator.required(`${field.label} is required`);
              }
              if (field.maxlength) {
                baseValidator = baseValidator.max(
                  parseInt('9'.repeat(field.maxlength)),
                  `${field.label} must be at most ${field.maxlength} digits`
                );
              }
            } else if (field.type === 'date') {
              baseValidator = Yup.date()
                .nullable()
                .transform((value, originalValue) => (originalValue === '' ? null : value))
                .typeError(`${field.label} must be a valid date`);
              if (field.required) {
                baseValidator = baseValidator.required(`${field.label} is required`);
              }
              if (field.validation === 'validateDOB') {
                baseValidator = baseValidator.max(
                  new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
                  `${field.label} must be at least 18 years old`
                );
              }
              if (field.validation === 'validateIssueDate') {
                baseValidator = baseValidator.max(new Date(), `${field.label} must be in the past`);
              }
              if (field.validation === 'validateExpiryDate') {
                baseValidator = baseValidator.min(new Date(), `${field.label} must be in the future`);
              }
            } else if (field.type === 'select' || field.type === 'radio') {
              baseValidator = Yup.string();
              if (field.required) {
                baseValidator = baseValidator.required(`${field.label} is required`);
              }
            }
            return baseValidator;
          },
          otherwise: () => Yup.mixed().nullable().notRequired(),
        });
      }
    } else {
      if (field.type === 'text' || field.type === 'email' || field.type === 'textarea') {
        validator = Yup.string();
        const skipGlobalRequired = primaryEmployerFields.includes(field.name);
        if (field.required && !skipGlobalRequired) {
          validator = validator.required(`${field.label} is required`);
        }
        if (field.validationPattern) {
          validator = validator.matches(
            new RegExp(field.validationPattern),
            field.validationMessage || `${field.label} is invalid`
          );
        }
        if (field.type === 'email') {
          validator = validator.email(`${field.label} must be a valid email`);
        }
        if (field.maxlength) {
          validator = validator.max(
            field.maxlength,
            `${field.label} must be at most ${field.maxlength} characters`
          );
        }
      } else if (field.type === 'number') {
        validator = Yup.number().typeError(`${field.label} must be a number`);
        const skipGlobalRequired = primaryEmployerFields.includes(field.name);
        if (field.required && !skipGlobalRequired) {
          validator = validator.required(`${field.label} is required`);
        }
        if (field.maxlength) {
          validator = validator.max(
            parseInt('9'.repeat(field.maxlength)),
            `${field.label} must be at most ${field.maxlength} digits`
          );
        }
      } else if (field.type === 'date') {
        validator = Yup.date()
          .nullable()
          .transform((value, originalValue) => (originalValue === '' ? null : value))
          .typeError(`${field.label} must be a valid date`);
        const skipGlobalRequired = primaryEmployerFields.includes(field.name);
        if (field.required && !skipGlobalRequired) {
          validator = validator.required(`${field.label} is required`);
        }
        if (field.validation === 'validateDOB') {
          validator = validator.max(
            new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
            `${field.label} must be at least 18 years old`
          );
        }
        if (field.validation === 'validateIssueDate') {
          validator = validator.max(new Date(), `${field.label} must be in the past`);
        }
        if (field.validation === 'validateExpiryDate') {
          validator = validator.min(new Date(), `${field.label} must be in the future`);
        }
      } else if (field.type === 'select' || field.type === 'radio') {
        validator = Yup.string();
        const skipGlobalRequired = primaryEmployerFields.includes(field.name);
        if (field.required && !skipGlobalRequired) {
          validator = validator.required(`${field.label} is required`);
        }
      }
    }

    if (validator) {
      shape[field.name] = validator;
    }


    if (field.conditional && field.conditional.fields) {
      field.conditional.fields.forEach(addFieldToSchema);
    }
  };

  fields.forEach(addFieldToSchema);

  return Yup.object().shape(shape);
};